import React, { useState } from "react";
import { toast } from "sonner";
import { Lightning, Eye, EyeSlash } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { useSiteSettings } from "@/context/SiteSettingsContext";

// What features show on this page. Add new ones here as flags appear.
const FEATURES = [
  {
    key: "co_innovation_enabled",
    title: "Co-innovation programme",
    description:
      "When ON, the Co-innovation programme is publicly visible: the /co-innovation page renders, the footer link shows, demo cards display a 'Co-innovation' badge, and demo-page CTAs/sub-blocks reference it. When OFF, every public reference is hidden — the /co-innovation URL silently redirects to the homepage, demo badges and CTAs disappear, footer link disappears, even the digital business card co-innovation block hides. No 'coming soon' acknowledgement, no invitation. Flip back on whenever you're ready to make a fanfare reintroduction.",
    affects: [
      "/co-innovation page (silently redirects home)",
      "Footer link",
      "Demo catalogue 'Co-innovation' card badges",
      "Demo detail 'Co-innovation candidate' badge",
      "OrderJar / QuietRoom demo footer CTAs",
      "Event landing 'Co-innovation' explore card",
      "Digital business card 'Founding-partner places' block",
    ],
  },
  {
    key: "co_innovation_just_enabled_banner",
    title: "Homepage 'Just reopened' banner",
    description:
      "Optional fanfare banner that appears at the very top of the homepage announcing Co-innovation has just reopened. Use it when you flip the programme back on — switch this OFF again once the moment has passed. The banner is dismissible per-visitor (localStorage), so each customer only sees it once.",
    affects: [
      "Homepage hero — pinned banner at the top",
    ],
  },
];

export default function Features() {
  const { settings, refresh } = useSiteSettings();
  const [saving, setSaving] = useState({});

  const toggle = async (key, current) => {
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      await api.put("/admin/settings", { [key]: !current });
      await refresh();
      toast.success(!current ? "Feature enabled" : "Feature disabled");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update");
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
    }
  };

  return (
    <div className="p-8 md:p-10 max-w-4xl" data-testid="admin-features">
      <p className="overline mb-2">Site features</p>
      <h1 className="text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Feature switches</h1>
      <p className="text-sm text-zinc-600 mb-8 max-w-2xl">Master kill-switches for site-wide programmes and CTAs. Useful when you need to temporarily hide an offering without removing the underlying pages or data.</p>

      <div className="space-y-4">
        {FEATURES.map((f) => {
          const on = !!settings[f.key];
          const busy = !!saving[f.key];
          return (
            <div key={f.key} className="bg-white border border-zinc-200 p-6" data-testid={`feature-card-${f.key}`}>
              <div className="flex items-start justify-between gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Lightning size={16} weight="fill" className="text-[hsl(var(--appistry-signal))]" />
                    <h2 className="font-semibold tracking-tight">{f.title}</h2>
                    <span
                      className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 ${on ? "bg-emerald-100 text-emerald-700" : "bg-zinc-200 text-zinc-600"}`}
                      data-testid={`feature-status-${f.key}`}
                    >
                      {on ? "Live" : "Hidden"}
                    </span>
                  </div>
                  <p className="text-sm text-zinc-600 mb-3">{f.description}</p>
                  <div className="text-xs text-zinc-500">
                    <span className="font-mono uppercase tracking-widest mr-2">Affects:</span>
                    {f.affects.join(" · ")}
                  </div>
                </div>
                <button
                  onClick={() => toggle(f.key, on)}
                  disabled={busy}
                  className={`inline-flex items-center gap-2 px-4 py-2 text-xs font-mono uppercase tracking-widest border transition-colors disabled:opacity-50 ${on ? "border-zinc-200 text-zinc-700 hover:bg-zinc-50" : "bg-zinc-950 text-white border-zinc-950 hover:bg-zinc-800"}`}
                  data-testid={`feature-toggle-${f.key}`}
                >
                  {on ? <><EyeSlash size={14} weight="bold" /> Hide</> : <><Eye size={14} weight="bold" /> Enable</>}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
